// Two separate things, both optional and both silent when unconfigured.
//
// 1. The Cloudflare Web Analytics beacon, injected only when a token exists.
// 2. A ping to our own Netlify Function on each page view, which records the
//    visit with the country and city Netlify attaches to the request.
//
// Neither stores an IP address. Cloudflare does not expose them, and the
// function deliberately drops the one it can see. That is what keeps this out
// of personal-data territory and means the site needs no cookie banner.

export default defineNuxtPlugin((nuxtApp) => {
  const { cfBeaconToken, visitLog } = useRuntimeConfig().public;

  if (cfBeaconToken) {
    // Appended after mount rather than declared in head: it is analytics, and
    // it should never sit in front of the page rendering.
    const beacon = document.createElement('script');
    beacon.src = 'https://static.cloudflareinsights.com/beacon.min.js';
    beacon.defer = true;
    beacon.setAttribute('data-cf-beacon', JSON.stringify({ token: cfBeaconToken }));
    document.head.appendChild(beacon);
  }

  if (!visitLog) return;

  const router = useRouter();
  let last = null;

  function record(path) {
    // The horizontal track means one "page" can be navigated within many
    // times; only a genuine path change is a new view.
    if (path === last) return;
    last = path;

    const payload = JSON.stringify({
      path,
      referrer: document.referrer || null,
      // Coarse only. Enough to tell a phone from a desktop without building a
      // fingerprint out of it.
      screen: window.innerWidth < 769 ? 'mobile' : 'desktop',
    });

    // sendBeacon survives the page being closed mid-request, which a fetch
    // does not. It also cannot be awaited, which is the point: nothing here
    // should ever delay a navigation.
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/.netlify/functions/visit', new Blob([payload], { type: 'application/json' }));
      return;
    }

    fetch('/.netlify/functions/visit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {
      // A failed analytics ping is not worth surfacing to anybody.
    });
  }

  nuxtApp.hook('app:mounted', () => record(router.currentRoute.value.path));
  router.afterEach((to) => record(to.path));
});
